import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Event types
export const EventTypes = {
  TRADE_EXECUTED: 'trade_executed',
  TRADE_PENDING: 'trade_pending',
  TRADE_FAILED: 'trade_failed',
  AGENT_CREATED: 'agent_created',
  AGENT_UPDATED: 'agent_updated',
  AGENT_KILLED: 'agent_killed',
  AGENT_CLONED: 'agent_cloned',
  AGENT_MUTATED: 'agent_mutated',
  CIRCUIT_BREAKER_TRIGGERED: 'circuit_breaker_triggered',
  CIRCUIT_BREAKER_RESET: 'circuit_breaker_reset',
  EMERGENCY_STOP: 'emergency_stop',
  SYSTEM_RESUMED: 'system_resumed',
  METRICS_UPDATE: 'metrics_update',
  PROFIT_UPDATE: 'profit_update',
  RANKING_UPDATE: 'ranking_update',
  SCHEDULER_TICK: 'scheduler_tick',
  SCHEDULER_STARTED: 'scheduler_started',
  SCHEDULER_STOPPED: 'scheduler_stopped',
  AUTONOMOUS_ACTIVATED: 'autonomous_activated',
  AUTONOMOUS_DEACTIVATED: 'autonomous_deactivated',
  // HFT Bot Events
  OPPORTUNITY_DETECTED: 'opportunity_detected',
  ARBITRAGE_FOUND: 'arbitrage_found',
  POSITION_OPENED: 'position_opened',
  POSITION_CLOSED: 'position_closed',
  MARKET_RESOLVED: 'market_resolved',
  BOT_STATUS_UPDATE: 'bot_status_update',
  SCAN_COMPLETED: 'scan_completed',
  // Revenue & Referrals
  REVENUE_EARNED: 'revenue_earned',
  REFERRAL_COMMISSION: 'referral_commission',
  WITHDRAWAL_PROCESSED: 'withdrawal_processed',
  // General
  NOTIFICATION: 'notification',
  HEARTBEAT: 'heartbeat',
};

export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const eventListenersRef = useRef({});
  const maxEvents = 100;

  const getWsUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${wsHost}/api/ws?token=${token}`;
  }, []);

  const connect = useCallback(() => {
    const url = getWsUrl();
    if (!url) {
      console.log('WebSocket: No token available, skipping connection');
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      console.log('WebSocket: Connecting...');
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        console.log('WebSocket: Connected');
        setIsConnected(true);
        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Skip heartbeat and pong messages from UI updates
          if (data.type === 'heartbeat' || data.type === 'pong' || data.type === 'connected') {
            return;
          }

          // Store event
          setLastEvent(data);
          setEvents(prev => {
            const newEvents = [data, ...prev].slice(0, maxEvents);
            return newEvents;
          });

          // Notify listeners
          const listeners = eventListenersRef.current[data.type] || [];
          listeners.forEach(listener => {
            try {
              listener(data);
            } catch (e) {
              console.error('WebSocket listener error:', e);
            }
          });

          // Show toast for important events
          showEventToast(data);
        } catch (e) {
          console.error('WebSocket: Error parsing message', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket: Disconnected', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect after 5 seconds (unless it was a clean close)
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('WebSocket: Attempting reconnect...');
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket: Error', error);
      };
    } catch (error) {
      console.error('WebSocket: Connection error', error);
    }
  }, [getWsUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((type, data = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...data }));
    }
  }, []);

  const subscribe = useCallback((eventType, callback) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = [];
    }
    eventListenersRef.current[eventType].push(callback);

    // Return unsubscribe function
    return () => {
      eventListenersRef.current[eventType] = eventListenersRef.current[eventType].filter(
        cb => cb !== callback
      );
    };
  }, []);

  const showEventToast = (event) => {
    const { type, data } = event;

    switch (type) {
      case EventTypes.TRADE_EXECUTED:
        toast.success(`Trade ejecutado: ${data.action} $${data.amount} ${data.asset}`, {
          description: data.profit ? `Profit: $${data.profit.toFixed(2)}` : undefined,
        });
        break;

      case EventTypes.AGENT_KILLED:
        toast.error(`Agente eliminado: ${data.agent_name}`, {
          description: data.reason,
        });
        break;

      case EventTypes.AGENT_CLONED:
        toast.success(`Agente clonado: ${data.child_name}`, {
          description: `Padre: ${data.parent_name}`,
        });
        break;

      case EventTypes.AGENT_MUTATED:
        toast.info(`Agente mutado: ${data.child_name}`, {
          description: `${data.mutations?.length || 0} mutaciones aplicadas`,
        });
        break;

      case EventTypes.CIRCUIT_BREAKER_TRIGGERED:
        toast.warning(`Circuit Breaker activado: ${data.breaker_name}`, {
          description: data.reason,
        });
        break;

      case EventTypes.EMERGENCY_STOP:
        toast.error('⚠️ EMERGENCY STOP ACTIVADO', {
          description: 'El sistema ha sido detenido por seguridad',
          duration: 10000,
        });
        break;

      case EventTypes.AUTONOMOUS_ACTIVATED:
        toast.success('🚀 Modo Autónomo Activado', {
          description: 'El sistema está generando dinero automáticamente',
        });
        break;

      case EventTypes.AUTONOMOUS_DEACTIVATED:
        toast.info('Modo Autónomo Desactivado', {
          description: 'El sistema ha sido pausado',
        });
        break;

      // HFT Bot Events
      case EventTypes.OPPORTUNITY_DETECTED:
        toast.success(`🎯 Oportunidad detectada!`, {
          description: `${data.outcome} @ $${data.price?.toFixed(4)} - ${data.confidence?.toFixed(0)}% certeza`,
        });
        break;

      case EventTypes.ARBITRAGE_FOUND:
        toast.success(`⚡ ARBITRAJE detectado!`, {
          description: `YES+NO = $${data.total?.toFixed(4)} - Profit: +${data.profit_pct?.toFixed(2)}%`,
        });
        break;

      case EventTypes.POSITION_OPENED:
        toast.success(`📈 Posición abierta`, {
          description: `${data.outcome} @ $${data.price?.toFixed(4)} - Costo: $${data.cost?.toFixed(2)}`,
        });
        break;

      case EventTypes.POSITION_CLOSED:
        const pnlEmoji = data.net_pnl >= 0 ? '🎉' : '😢';
        const pnlColor = data.net_pnl >= 0 ? 'success' : 'error';
        (data.net_pnl >= 0 ? toast.success : toast.error)(
          `${pnlEmoji} Posición cerrada - ${data.reason}`,
          { description: `P&L Neto: $${data.net_pnl?.toFixed(2)} (Fee: $${data.platform_fee?.toFixed(2)})` }
        );
        break;

      case EventTypes.MARKET_RESOLVED:
        toast.info(`📊 Mercado resuelto: ${data.winning_outcome}`, {
          description: `${data.positions_affected} posiciones afectadas`,
        });
        break;

      case EventTypes.SCAN_COMPLETED:
        // Only show if opportunities found
        if (data.opportunities_found > 0) {
          toast.info(`🔍 Scan #${data.scan_number} completado`, {
            description: `${data.opportunities_found} oportunidades encontradas`,
          });
        }
        break;

      // Revenue & Referral Events
      case EventTypes.REVENUE_EARNED:
        toast.success(`💰 Revenue ganado: $${data.amount?.toFixed(2)}`, {
          description: `Fuente: ${data.source}`,
        });
        break;

      case EventTypes.REFERRAL_COMMISSION:
        toast.success(`👥 Comisión de referido: $${data.amount?.toFixed(2)}`, {
          description: `Nivel ${data.level} - De: ${data.from_user_id}`,
        });
        break;

      case EventTypes.NOTIFICATION:
        const toastFn = {
          info: toast.info,
          success: toast.success,
          warning: toast.warning,
          error: toast.error,
        }[data.level] || toast.info;
        toastFn(data.title, { description: data.message });
        break;

      default:
        // Don't show toast for other events
        break;
    }
  };

  // Connect when component mounts and token is available
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      connect();
    }

    // Listen for storage changes (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          connect();
        } else {
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      disconnect();
    };
  }, [connect, disconnect]);

  // Send ping every 25 seconds to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      sendMessage('ping');
    }, 25000);

    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  const value = {
    isConnected,
    lastEvent,
    events,
    connect,
    disconnect,
    sendMessage,
    subscribe,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

// Hook for subscribing to specific event types
export function useWebSocketEvent(eventType, callback) {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubscribe = subscribe(eventType, callback);
    return unsubscribe;
  }, [eventType, callback, subscribe]);
}

export default WebSocketProvider;
